import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  AlertTriangle, CheckCircle, Lightbulb, TrendingUp, Target, Users, DollarSign,
  Shield, Handshake, Leaf, BookOpen, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip
} from 'recharts';

const API = BACKEND_URL;

const DIM_ICONS = {
  problem_clarity: Target,
  target_groups: Users,
  innovation: Lightbulb,
  financial_feasibility: DollarSign,
  social_impact: TrendingUp,
  implementation_readiness: Shield,
  sustainability: Leaf,
  stakeholder_engagement: Handshake,
};

const MATURITY_CONFIG = {
  early_idea: { label: 'Early Idea', color: '#EF4444', bg: 'bg-red-50 border-red-200 text-red-700' },
  developing: { label: 'Developing', color: '#F59E0B', bg: 'bg-amber-50 border-amber-200 text-amber-700' },
  mature: { label: 'Mature', color: '#3B82F6', bg: 'bg-blue-50 border-blue-200 text-blue-700' },
  ready: { label: 'Ready for Investment', color: '#10B981', bg: 'bg-green-50 border-green-200 text-green-700' },
};

const InvestmentSelfAssessment = ({ investmentId, existingAssessment, onAssessmentSaved }) => {
  const [dimensions, setDimensions] = useState([]);
  const [maturityLevels, setMaturityLevels] = useState([]);
  const [scores, setScores] = useState({});
  const [assessment, setAssessment] = useState(existingAssessment);
  const [showEditor, setShowEditor] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchDimensions();
  }, []);

  useEffect(() => {
    setAssessment(existingAssessment);
  }, [existingAssessment]);

  const fetchDimensions = async () => {
    try {
      const res = await axios.get(`${API}/api/social-investments/assessment-dimensions`, { headers });
      setDimensions(res.data.dimensions || []);
      setMaturityLevels(res.data.maturity_levels || []);
    } catch {
      // Dimensions will be empty
    } finally {
      setLoading(false);
    }
  };

  const openEditor = () => {
    const init = {};
    dimensions.forEach(d => {
      const existing = assessment?.scores?.[d.id];
      init[d.id] = { score: existing?.score || 0, notes: existing?.notes || '' };
    });
    setScores(init);
    setShowEditor(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/social-investments/${investmentId}/assessment`, { scores }, { headers });
      setAssessment(res.data);
      setShowEditor(false);
      toast.success('Self-assessment saved');
      if (onAssessmentSaved) onAssessmentSaved(res.data);
    } catch {
      toast.error('Failed to save assessment');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  const maturity = assessment?.maturity_level || 'early_idea';
  const mc = MATURITY_CONFIG[maturity] || MATURITY_CONFIG.early_idea;
  const totalScore = assessment?.total_score || 0;

  // Radar chart data
  const radarData = dimensions.map(d => ({
    dimension: d.title.split(' ').slice(0, 2).join(' '),
    score: assessment?.scores?.[d.id]?.score || 0,
    fullMark: 5,
  }));

  return (
    <div className="space-y-4" data-testid="self-assessment-section">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-blue-500" /> Idea Maturity Assessment
        </h3>
        <Button size="sm" variant="outline" onClick={openEditor} data-testid="start-assessment-btn">
          {assessment?.total_score > 0 ? 'Retake Assessment' : 'Start Assessment'}
        </Button>
      </div>

      {!assessment || totalScore === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <CheckCircle className="h-8 w-8 text-gray-300 mb-2" />
            <p className="text-sm text-gray-500 text-center">No assessment yet. Take the self-assessment to evaluate your idea's maturity.</p>
            <Button size="sm" className="mt-3 bg-blue-600 hover:bg-blue-700" onClick={openEditor}>Start Self-Assessment</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Score & Maturity */}
          <Card className={`border ${mc.bg}`}>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-gray-500 mb-1">Maturity Level</p>
              <p className="text-lg font-bold" style={{ color: mc.color }}>{mc.label}</p>
              <p className="text-4xl font-bold mt-2" style={{ color: mc.color }}>{totalScore}</p>
              <p className="text-xs text-gray-500">/ 5.0</p>
              <Progress value={totalScore / 5 * 100} className="h-2 mt-3" />
              {/* Maturity scale */}
              <div className="flex justify-between mt-3 text-xs text-gray-500">
                {Object.values(MATURITY_CONFIG).map(m => (
                  <span key={m.label} style={{ color: m.color }}>{m.label.split(' ')[0]}</span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Radar */}
          <Card className="lg:col-span-2">
            <CardContent className="p-2">
              <ResponsiveContainer width="100%" height={220}>
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="dimension" fontSize={10} />
                  <PolarRadiusAxis domain={[0, 5]} tickCount={6} fontSize={9} />
                  <Radar dataKey="score" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.2} strokeWidth={2} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Dimension breakdown */}
          <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-2">
            {dimensions.map(d => {
              const s = assessment?.scores?.[d.id]?.score || 0;
              const Icon = DIM_ICONS[d.id] || Target;
              return (
                <div key={d.id} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border">
                  <Icon className="h-4 w-4 text-gray-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-600 truncate">{d.title}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      {[1, 2, 3, 4, 5].map(n => (
                        <div key={n} className={`w-2 h-2 rounded-full ${n <= s ? 'bg-blue-500' : 'bg-gray-200'}`} />
                      ))}
                      <span className="text-xs font-medium text-gray-700 ml-1">{s}/5</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Recommendations */}
          {(assessment.recommendations || []).length > 0 && (
            <Card className="lg:col-span-3 bg-amber-50/50 border-amber-100">
              <CardContent className="p-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <Lightbulb className="h-4 w-4 text-amber-700" />
                  <span className="text-sm font-semibold text-amber-700">Recommendations to Improve</span>
                </div>
                <ul className="space-y-1.5">
                  {assessment.recommendations.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
                      <ChevronRight className="h-3 w-3 text-amber-400 mt-0.5 shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
                {totalScore < 3.5 && (
                  <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-100 flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-blue-500" />
                    <span className="text-xs text-blue-700">Access the <strong>Capacity Building</strong> module for training resources.</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Assessment Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Self-Assessment: Idea Maturity</DialogTitle></DialogHeader>
          <p className="text-xs text-gray-500 mb-4">Rate each dimension from 1 (very low) to 5 (excellent). This helps evaluate how ready your investment idea is.</p>
          <div className="space-y-4">
            {dimensions.map(dim => {
              const Icon = DIM_ICONS[dim.id] || Target;
              const s = scores[dim.id]?.score || 0;
              return (
                <div key={dim.id} className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="h-4 w-4 text-blue-500" />
                    <span className="text-sm font-semibold text-gray-900">{dim.title}</span>
                    <Badge variant="outline" className="text-xs">{Math.round(dim.weight * 100)}%</Badge>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">{dim.description}</p>
                  {/* Guiding questions */}
                  <div className="mb-2 pl-6">
                    {(dim.questions || []).map((q, qi) => (
                      <p key={qi} className="text-xs text-gray-500 leading-relaxed">- {q}</p>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(n => (
                        <button
                          key={n}
                          onClick={() => setScores(prev => ({ ...prev, [dim.id]: { ...prev[dim.id], score: n } }))}
                          className={`w-9 h-9 rounded-full text-sm font-medium transition-colors ${s >= n ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          data-testid={`assess-${dim.id}-${n}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <Input
                      placeholder="Notes (optional)..."
                      className="flex-1 h-8 text-xs"
                      value={scores[dim.id]?.notes || ''}
                      onChange={e => setScores(prev => ({ ...prev, [dim.id]: { ...prev[dim.id], notes: e.target.value } }))}
                    />
                  </div>
                </div>
              );
            })}
            <div className="flex justify-end gap-3 pt-3 border-t">
              <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-assessment-btn">
                {saving ? 'Saving...' : 'Save Assessment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvestmentSelfAssessment;
